import uniqBy from "lodash/uniqBy";
import { usePropertyFieldValueChange } from "PFApp/hooks/activity_form/use_property_field_value_change";
import { useVisiblePermittedProperties } from "PFApp/hooks/activity_form/use_visible_permitted_properties";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import {
  ActivityPureCustomValue,
  Profile,
  PureCustomValue,
  SkillsFrameworkCustomValue,
  Subtemplate,
  Template
} from "PFCore/types";

import {
  SkillsFrameworkCustomField,
  SkillsFrameworkCustomValueDecoratedMerged,
  SkillsFrameworkSkillDecoratedMerged
} from "../skills_frameworks_side_panel.types";

type UsePopulateSkillsFrameworks = {
  template: Template;
  subtemplate: Subtemplate | null;
  activityCreator: Profile | null;
  roleFormKeyPrefix?: string;
  onPostPopulate?: (data: FormFieldData[]) => void;
};

export const usePopulateSkillsFrameworks = ({
  template,
  subtemplate,
  activityCreator,
  roleFormKeyPrefix,
  onPostPopulate
}: UsePopulateSkillsFrameworks) => {
  const { getVisiblePermittedProperties } = useVisiblePermittedProperties();
  const changePropertyFieldValue = usePropertyFieldValueChange({
    template,
    subtemplate,
    activityCreator,
    roleFormKeyPrefix
  });

  const populateSkillsFrameworksValues = (
    skills: SkillsFrameworkSkillDecoratedMerged[],
    customFields: SkillsFrameworkCustomField<SkillsFrameworkCustomValueDecoratedMerged>[]
  ): void => {
    const activitySkills: ActivityPureCustomValue[] = skills.map(
      ({ skillId, value, importance, requiredExperience }) => ({
        id: skillId,
        text: value,
        importance,
        type: "skills",
        required_experience: requiredExperience
      })
    );

    const activityCustomFields: {
      [name: string]: (SkillsFrameworkCustomValue | PureCustomValue)[];
    } = customFields.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.type.name]: curr.values.map(({ customValueId, value }) => ({ id: customValueId, value }))
      }),
      {}
    );
    activityCustomFields.skills = activitySkills;

    const visibleProperties = getVisiblePermittedProperties({ subtemplate, template, activityCreator });
    const dataToPopulate: FormFieldData[] = Object.entries(activityCustomFields)
      .filter(([fieldName]) => {
        const property = visibleProperties.find(({ name }) => name === fieldName);
        // SF consists of CFs only. We don't want to populate CF value into non Custom Field template input
        // that is named the same as SF custom field
        return property?.type === "custom_field";
      })
      .map(([fieldName, values]) => ({
        values: uniqBy(values, "id"),
        fieldName
      }));

    dataToPopulate.forEach(({ fieldName, values }) => changePropertyFieldValue(fieldName, values));
    onPostPopulate?.(dataToPopulate);
  };

  return {
    populateSkillsFrameworksValues
  };
};
